// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { BackButton } from '../components/common';
import { useNavigate } from 'react-router-dom';

const TermsConditionsPage = ({ onBack }) => {
  const navigate = useNavigate();
  const renderHeader = () => {
    if (onBack) {
      return (
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <BackButton onClick={onBack} label="← More" className="mb-2" />
            <h1 className="text-2xl font-bold text-gray-900">Terms & Conditions</h1>
          </div>
        </header>
      );
    }

    return (
      <nav className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span>
              </span>
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-sm sm:text-base text-gray-700 hover:text-blue-600 font-semibold transition whitespace-nowrap"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {renderHeader()}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 md:p-12">
          {!onBack && <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>}
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> 11 February 2026<br />
            <strong>Effective Date:</strong> 19 July 2026<br />
            <strong>Version:</strong> 3.0 (Comprehensive Edition)
          </p>

          {/* Table of Contents */}
          <div className="bg-gray-100 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Table of Contents (26 Sections)</h3>
            <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-700">
              <div>
                <p>1. Agreement to Terms</p>
                <p>2. Definitions</p>
                <p>3. Service Description & Our Role</p>
                <p>4. Account Registration & Security</p>
                <p>5. Subscription Plans & Payment</p>
                <p>6. Cancellation & Refunds</p>
                <p className="text-red-600 font-semibold">7. Prohibited Activities & Anti-Scraping</p>
                <p className="text-red-600 font-semibold">8. Liquidated Damages & Penalties</p>
                <p>9. Intellectual Property Rights</p>
                <p>10. Indemnification</p>
                <p>11. Disclaimers & Limitation of Liability</p>
                <p>12. Termination</p>
                <p>13. Governing Law & Dispute Resolution</p>
              </div>
              <div>
                <p>14. General Provisions</p>
                <p>15. Data Protection & Privacy</p>
                <p>16. Third-Party Links & Services</p>
                <p>17. Copyright Infringement Claims</p>
                <p className="text-blue-600 font-semibold">18. Cookies & Tracking Technologies</p>
                <p className="text-blue-600 font-semibold">19. User-Generated Content</p>
                <p className="text-emerald-700 font-semibold">20. Loyalty &amp; Referral Rewards Programme</p>
                <p className="text-blue-600 font-semibold">21. Family Plan Terms</p>
                <p className="text-purple-700 font-semibold">22. Founder's Club &amp; Beta Programme</p>
                <p className="text-blue-600 font-semibold">23. Mobile Application Terms</p>
                <p className="text-gray-600 font-semibold">24. API Terms of Service</p>
                <p className="text-orange-600 font-semibold">25. Dispute Resolution & Class Action Waiver</p>
                <p>26. Contact Information</p>
              </div>
            </div>
          </div>

          {/* Critical Notice Banner */}
          <div className="bg-red-50 border-2 border-red-500 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-bold text-red-700 mb-3">⚠️ IMPORTANT LEGAL NOTICE</h3>
            <p className="text-red-700 mb-2">
              This Agreement contains legally binding terms regarding <strong>data protection, anti-scraping provisions, intellectual property rights, and liquidated damages</strong>. By accessing this website, you agree to be bound by ALL terms herein.
            </p>
            <p className="text-red-700 font-semibold">
              Unauthorized data extraction, scraping, or copying carries <strong>minimum penalties of £50,000</strong> per violation.
            </p>
          </div>

          <div className="space-y-8 text-gray-700">
            {/* Section 1: Agreement to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
              <p className="mb-4">
                This document constitutes a legally binding agreement ("Agreement") between you ("User", "You", or "Your") and PricePAL Compare Ltd (Company Registration Number: 17031232, ICO Registration: ZC094153), a company registered in England and Wales ("PricePAL", "We", "Us", or "Our"), with registered address at 20 Wenlock Road, London, N1 7GU, governing your access to and use of the PricePAL website, mobile applications, APIs, and all related services (collectively, the "Service").
              </p>
              <p className="mb-4">
                By accessing, browsing, or using the Service in any manner, including but not limited to visiting or browsing the website, you acknowledge that you have read, understood, and agree to be bound by this Agreement and our Privacy Policy.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <p className="font-semibold text-yellow-800">
                  <strong>BINDING EFFECT:</strong> If you do not agree to ALL terms of this Agreement, you must immediately cease all use of the Service. Continued use constitutes acceptance and creates a binding contract.
                </p>
              </div>
            </section>

            {/* Section 2: Definitions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
              <p className="mb-4">For the purposes of this Agreement:</p>
              <ul className="list-disc pl-6 space-y-3">
                <li><strong>"Content"</strong> means all text, data, information, graphics, photographs, images, illustrations, audio clips, video clips, software, and other materials appearing on or available through the Service.</li>
                <li><strong>"Database"</strong> means the compilation, collection, arrangement, and assembly of all Content, including but not limited to price data, product information, retailer data, user-generated content, and metadata.</li>
                <li><strong>"Scraping"</strong> means any automated or manual process of extracting, copying, harvesting, crawling, indexing, or collecting Content or data from the Service.</li>
                <li><strong>"Automated Means"</strong> includes but is not limited to bots, spiders, crawlers, scrapers, scripts, browser extensions, data mining tools, robots, or any other automated software, device, or mechanism.</li>
                <li><strong>"Competitor"</strong> means any person or entity engaged in or planning to engage in a business that competes with PricePAL Compare, including price comparison services, grocery aggregators, or similar platforms.</li>
                <li><strong>"Commercial Use"</strong> means any use intended to generate revenue, support business operations, or provide competitive advantage.</li>
              </ul>
            </section>

            {/* Section 3: Service Description */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Service Description & Our Role</h2>
              <p className="mb-4">
                PricePAL Compare is a price comparison and shopping assistance service for UK consumers. We provide:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Price comparisons across UK grocery retailers</li>
                <li>Shopping list management and optimization</li>
                <li>Price alerts and notifications</li>
                <li>Fuel price comparison</li>
                <li>Savings tracking and personalized recommendations</li>
                <li>Recipe and meal planning assistance</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Relationship with Retailers</h3>
              <p className="mb-4">
                PricePAL Compare is <strong>NOT</strong> a retailer, seller, or supplier of goods or services featured on the Service. We aggregate publicly available price information for consumer convenience. We may receive affiliate or referral commissions <strong>from retailers</strong> for sales generated through our Service. <strong>No portion of those retailer-side commissions is ever paid to End Users in cash, vouchers, gift cards, or any other monetary form.</strong> User-side rewards are governed exclusively by Section 20 (Loyalty &amp; Referral Rewards Programme) and are limited to free subscription time and gamification points only.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Information Accuracy</h3>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <p>
                  While we endeavour to provide accurate and current information, we make no guarantees regarding the accuracy, completeness, or timeliness of Content. Prices and availability are subject to change by retailers. <strong>Always verify prices directly with the retailer before making purchasing decisions.</strong>
                </p>
              </div>
            </section>

            {/* Section 4: Account Terms */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Account Registration & Security</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.1 Eligibility</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>You must be at least 16 years of age</li>
                <li>You must be a resident of the United Kingdom</li>
                <li>You must provide accurate, current, and complete registration information</li>
                <li>You must not be a Competitor or agent of a Competitor</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.2 Account Responsibilities</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>You are solely responsible for maintaining the confidentiality of your account credentials</li>
                <li>You must immediately notify us of any unauthorized access or security breach</li>
                <li>You are liable for all activities conducted through your account</li>
                <li>One account per individual – account sharing is strictly prohibited</li>
                <li>You must not create accounts on behalf of others or use false identities</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">4.3 Account Verification</h3>
              <p>
                We reserve the right to verify your identity, location, and eligibility at any time. Failure to provide requested verification may result in account suspension or termination.
              </p>
            </section>

            {/* Section 5: Subscription & Payment */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Subscription Plans & Payment</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.1 Free Trial (Premium / Family)</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>7-day free trial available for eligible new Premium or Family customers.</li>
                <li>A valid payment card is required at sign-up. Your card is authorised by Stripe (our payment processor) but is not charged during the 7-day trial period.</li>
                <li>After the trial ends, the applicable plan price will be charged to your card and the subscription will renew each billing period until you cancel.</li>
                <li>You may cancel at any time before the trial ends and you will not be charged.</li>
                <li>One trial per individual — creating multiple accounts to obtain additional trials is prohibited.</li>
                <li>A separate "Standard Free" plan (no card required) remains available if you prefer not to enter card details — see the pricing page.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.2 Subscription Tiers</h3>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-4 py-2 text-left">Plan</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Monthly</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Annual</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Features</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">FREE (Standard)</td>
                      <td className="border border-gray-300 px-4 py-2">£0</td>
                      <td className="border border-gray-300 px-4 py-2">£0</td>
                      <td className="border border-gray-300 px-4 py-2">5 scans/day, 3 price alerts, 2 receipts/month, Basic Fuel Finder</td>
                    </tr>
                    <tr className="bg-blue-50">
                      <td className="border border-gray-300 px-4 py-2 font-semibold">PREMIUM TRIAL</td>
                      <td className="border border-gray-300 px-4 py-2">£0 (7 days)</td>
                      <td className="border border-gray-300 px-4 py-2">Card required</td>
                      <td className="border border-gray-300 px-4 py-2">Full Premium/Family access for 7 days; converts to selected paid plan unless cancelled</td>
                    </tr>
                    <tr className="bg-yellow-50">
                      <td className="border border-gray-300 px-4 py-2 font-semibold">ALPHA TESTER</td>
                      <td className="border border-gray-300 px-4 py-2">£0</td>
                      <td className="border border-gray-300 px-4 py-2">£0</td>
                      <td className="border border-gray-300 px-4 py-2">Lifetime free access (50 spots, invite-only)</td>
                    </tr>
                    <tr className="bg-purple-50">
                      <td className="border border-gray-300 px-4 py-2 font-semibold">COMMUNITY</td>
                      <td className="border border-gray-300 px-4 py-2">£1.00</td>
                      <td className="border border-gray-300 px-4 py-2">£10.00/yr</td>
                      <td className="border border-gray-300 px-4 py-2">NHS, Blue Light Card, students &amp; teachers — verified ID required</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">PREMIUM (INDIVIDUAL)</td>
                      <td className="border border-gray-300 px-4 py-2">£1.99</td>
                      <td className="border border-gray-300 px-4 py-2">£19.99/yr</td>
                      <td className="border border-gray-300 px-4 py-2">Unlimited features, single account</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">FAMILY</td>
                      <td className="border border-gray-300 px-4 py-2">£2.99</td>
                      <td className="border border-gray-300 px-4 py-2">£29.99/yr</td>
                      <td className="border border-gray-300 px-4 py-2">Up to 4 family members</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">5.3 Payment Terms</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>All subscriptions automatically renew unless cancelled before the renewal date</li>
                <li>Payments are processed securely via Stripe</li>
                <li>All prices are in British Pounds Sterling (GBP) and include VAT where applicable</li>
                <li>We will provide at least 30 days' notice of any price changes</li>
                <li>Failed payments may result in service suspension</li>
              </ul>
            </section>

            {/* Section 6: Cancellation & Refunds — UK consumer-law compliant
                Rewritten 4 June 2026 to align with CRA 2015 + CCR 2013.
                Full policy at /refund-policy. */}
            <section data-testid="terms-section-cancellation-refunds">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Cancellation &amp; Refunds</h2>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-4">
                <p className="font-semibold text-emerald-900 mb-1">Plain-English summary</p>
                <p className="text-sm text-emerald-900">
                  You can cancel any time. You are <strong>legally entitled to a full refund within 14 days of your first payment under the Consumer Contracts Regulations 2013</strong>, and to a further statutory refund right under the Consumer Rights Act 2015 if the service is faulty, not as described, or not fit for purpose — including on renewals. The full Refund Policy is at{' '}
                  <a href="/refund-policy" className="text-violet-700 underline">pricepalcompare.com/refund-policy</a>.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Cancellation Rights</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>You may cancel your subscription at any time through <strong>Settings → Billing &amp; Subscription → Manage Subscription</strong> in the app.</li>
                <li>Alternatively, use the <a href="https://billing.stripe.com/p/login/8x29AM9Xu3EJ2xp9y783C00" className="text-green-700 underline" target="_blank" rel="noreferrer">Stripe billing portal</a>, or email <a href="mailto:support@pricepalcompare.com" className="text-green-700 underline">support@pricepalcompare.com</a>.</li>
                <li>Cancellation takes effect at the end of your current billing period — you keep full access to paid features until then.</li>
                <li>After cancellation, your account automatically moves to the Free plan. Your data (scan history, saved products, loyalty cards) is retained.</li>
                <li>You may re-subscribe at any time to restore paid features.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 14-Day Cooling-Off Refund (Consumer Contracts Regulations 2013)</h3>
              <p className="mb-2">
                Under the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, you have an <strong>unconditional statutory right to cancel and receive a full refund within 14 calendar days</strong> of placing your first subscription order. This right:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Applies to every initial subscription purchase — Standard, Premium, Pro, VIP Founder, Early Founder, promotional and discounted tiers alike.</li>
                <li>Does not require you to give a reason.</li>
                <li>Is honoured automatically via Settings → Subscription → Request Refund Now, or by emailing support.</li>
                <li>Refunds are processed within 14 days of the request (typically the same day) using the same payment method as the original transaction.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.3 Refunds After 14 Days &mdash; Consumer Rights Act 2015</h3>
              <p className="mb-2">
                The Consumer Rights Act 2015 entitles you to a refund (in full or pro-rata) <strong>at any time</strong> &mdash; including on subscription renewals &mdash; where the digital service is:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li>Not of satisfactory quality (including prolonged or repeated unavailability of paid features);</li>
                <li>Not as described on pricepalcompare.com or in promotional material (Misrepresentation Act 1967);</li>
                <li>Not fit for the particular purpose you made known to us;</li>
                <li>Charged in error, double-charged, or billed for a tier you did not select;</li>
                <li>Renewed after a valid cancellation request we failed to action.</li>
              </ul>
              <p className="text-sm text-gray-700">
                Where you have a right to a refund under the 2015 Act, we will not arbitrarily decline reasonable requests. We will provide a written reason in any case where a request cannot be honoured. See{' '}
                <a href="/refund-policy" className="text-violet-700 underline">/refund-policy</a> for the full procedure.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">6.4 Free Trial (Premium / Family)</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Eligible new users who start a Premium or Family subscription receive a 7-day free trial with full access to the paid features of the selected plan.</li>
                <li>A valid payment card is required at the start of the trial. Stripe (our payment processor) authorises the card but does not charge it during the trial.</li>
                <li>At the end of the 7-day trial, your card will be charged the applicable plan price (Premium £1.99/month or Family £2.99/month) and the subscription will continue to renew each billing period until you cancel.</li>
                <li>You can cancel at any time before the trial ends from your Settings page — you will not be charged.</li>
                <li>If you cancel after the trial has converted, you keep paid access until the end of the current billing period and are moved to the Standard Free plan.</li>
                <li>Users who prefer not to enter a card can sign up for the Standard Free plan instead (5 scans/day, 3 price alerts, 2 receipts/month, Basic Fuel Finder).</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.5 Termination by PricePAL Compare</h3>
              <p className="mb-2">
                We reserve the right to terminate accounts that materially breach these Terms (in particular Section 7 — Prohibited Activities). Where we terminate an account for fraud, abuse or unlawful conduct, no refund of the current period is payable. <strong>This carve-out does not affect your statutory rights</strong> in respect of any payments made for service that was unlawfully withheld or mis-described before the breach.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">6.6 Statutory Rights Preserved</h3>
              <p>
                Nothing in this Section 6 excludes or limits your statutory rights under the Consumer Rights Act 2015, the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, the Misrepresentation Act 1967, the Unfair Contract Terms Act 1977, or any other applicable UK consumer-protection legislation. Where any term conflicts with your statutory rights, your statutory rights prevail.
              </p>
            </section>

            {/* Section 7: PROHIBITED ACTIVITIES - CRITICAL */}
            <section className="bg-red-50 border-2 border-red-300 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-red-800 mb-4">7. PROHIBITED ACTIVITIES & ANTI-SCRAPING PROVISIONS</h2>
              
              <div className="bg-white border border-red-300 rounded-lg p-4 mb-6">
                <p className="text-red-800 font-bold text-lg mb-2">⚠️ CRITICAL LEGAL NOTICE</p>
                <p className="text-red-700">
                  Violations of this section constitute material breaches of this Agreement and may result in civil liability, criminal prosecution, and liquidated damages as specified in Section 8.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-red-800 mb-3">7.1 Strictly Prohibited Activities</h3>
              <p className="mb-4 text-gray-700">You expressly agree that you shall NOT, under any circumstances:</p>
              
              <div className="space-y-4">
                <div className="bg-white border-l-4 border-red-500 p-4">
                  <h4 className="font-bold text-red-800 mb-2">A. Data Extraction & Scraping</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Use any Automated Means to access, scrape, crawl, spider, harvest, mine, extract, copy, or collect any Content or data from the Service</li>
                    <li>Manually copy, transcribe, or extract substantial portions of Content from the Service</li>
                    <li>Use browser developer tools, extensions, or scripts to extract data</li>
                    <li>Access the Service through any means other than our provided web interface or official mobile applications</li>
                    <li>Circumvent, disable, or interfere with any security, access control, or anti-scraping measures</li>
                    <li>Use headless browsers, browser automation tools (including but not limited to Selenium, Puppeteer, Playwright), or similar technologies</li>
                  </ul>
                </div>

                <div className="bg-white border-l-4 border-red-500 p-4">
                  <h4 className="font-bold text-red-800 mb-2">B. Unauthorized Access & System Interference</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Access or attempt to access any systems, servers, networks, or data not intended for you</li>
                    <li>Probe, scan, or test the vulnerability of any system or network</li>
                    <li>Breach or circumvent any security or authentication measures</li>
                    <li>Interfere with, disrupt, or create an undue burden on the Service or connected networks</li>
                    <li>Introduce malware, viruses, worms, Trojan horses, or other harmful code</li>
                    <li>Perform denial-of-service attacks or similar disruptive activities</li>
                    <li>Access APIs without explicit written authorization</li>
                  </ul>
                </div>

                <div className="bg-white border-l-4 border-red-500 p-4">
                  <h4 className="font-bold text-red-800 mb-2">C. Data Misuse & Commercial Exploitation</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Use any Content for Commercial Use without our express written consent</li>
                    <li>Sell, license, sublicense, transfer, or distribute any Content or data</li>
                    <li>Create derivative works, databases, or compilations using our Content</li>
                    <li>Use Content to train, develop, or improve any machine learning or artificial intelligence systems</li>
                    <li>Build or assist in building a competitive product or service</li>
                    <li>Aggregate our data with data from other sources for commercial purposes</li>
                    <li>Republish, display, or transmit Content on any other platform or service</li>
                  </ul>
                </div>

                <div className="bg-white border-l-4 border-red-500 p-4">
                  <h4 className="font-bold text-red-800 mb-2">D. Account & Identity Abuse</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Create multiple accounts or use false identities</li>
                    <li>Share account credentials or allow others to use your account</li>
                    <li>Access the Service on behalf of a Competitor</li>
                    <li>Impersonate any person or entity</li>
                    <li>Provide false, misleading, or inaccurate information</li>
                    <li>Use the Service to conduct competitive intelligence or market research for competitors</li>
                  </ul>
                </div>

                <div className="bg-white border-l-4 border-red-500 p-4">
                  <h4 className="font-bold text-red-800 mb-2">E. Other Prohibited Conduct</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Violate any applicable law, regulation, or third-party rights</li>
                    <li>Engage in fraudulent, deceptive, or misleading practices</li>
                    <li>Use the Service to send spam or unsolicited communications</li>
                    <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of any software</li>
                    <li>Remove, alter, or obscure any copyright, trademark, or proprietary notices</li>
                    <li>Encourage or assist any third party in doing any of the above</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-red-800 mb-3 mt-6">7.2 Automated Access Policy</h3>
              <p className="mb-4 text-gray-700">
                Access to the Service is permitted <strong>only</strong> through standard web browsers operated manually by individual human users for personal, non-commercial purposes. Any form of automated access is strictly prohibited except:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Search engine crawlers that comply with our robots.txt file</li>
                <li>Authorized partners with written API agreements</li>
              </ul>

              <h3 className="text-xl font-semibold text-red-800 mb-3 mt-6">7.3 Detection & Enforcement</h3>
              <p className="text-gray-700">
                We employ advanced technical measures to detect and prevent unauthorized access, including but not limited to: rate limiting, behavioral analysis, fingerprinting, CAPTCHA challenges, IP blocking, and legal action. By using the Service, you consent to these measures.
              </p>
            </section>

            {/* Section 8: LIQUIDATED DAMAGES - CRITICAL */}
            <section className="bg-yellow-50 border-2 border-yellow-500 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-yellow-800 mb-4">8. LIQUIDATED DAMAGES & PENALTIES</h2>
              
              <p className="mb-4 text-gray-700">
                You acknowledge and agree that violations of Section 7 cause substantial harm to PricePAL Compare that is difficult to quantify. Accordingly, you agree to pay the following liquidated damages, which represent a genuine pre-estimate of loss:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border-2 border-yellow-500 bg-white">
                  <thead className="bg-yellow-100">
                    <tr>
                      <th className="border border-yellow-500 px-4 py-3 text-left text-yellow-800">Violation Type</th>
                      <th className="border border-yellow-500 px-4 py-3 text-left text-yellow-800">Minimum Damages</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-yellow-500 px-4 py-3">Automated scraping or crawling</td>
                      <td className="border border-yellow-500 px-4 py-3 font-bold text-red-700">£50,000 per incident</td>
                    </tr>
                    <tr className="bg-yellow-50">
                      <td className="border border-yellow-500 px-4 py-3">Commercial use of scraped data</td>
                      <td className="border border-yellow-500 px-4 py-3 font-bold text-red-700">£100,000 + actual damages</td>
                    </tr>
                    <tr>
                      <td className="border border-yellow-500 px-4 py-3">Database extraction (substantial portion)</td>
                      <td className="border border-yellow-500 px-4 py-3 font-bold text-red-700">£250,000 + profits disgorgement</td>
                    </tr>
                    <tr className="bg-yellow-50">
                      <td className="border border-yellow-500 px-4 py-3">API access without authorization</td>
                      <td className="border border-yellow-500 px-4 py-3 font-bold text-red-700">£25,000 per unauthorized call</td>
                    </tr>
                    <tr>
                      <td className="border border-yellow-500 px-4 py-3">Security circumvention</td>
                      <td className="border border-yellow-500 px-4 py-3 font-bold text-red-700">£75,000 per incident</td>
                    </tr>
                    <tr className="bg-yellow-50">
                      <td className="border border-yellow-500 px-4 py-3">Building competitive product using our data</td>
                      <td className="border border-yellow-500 px-4 py-3 font-bold text-red-700">£500,000 + injunctive relief</td>
                    </tr>
                    <tr>
                      <td className="border border-yellow-500 px-4 py-3">AI/ML training using our data</td>
                      <td className="border border-yellow-500 px-4 py-3 font-bold text-red-700">£200,000 per model trained</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-white border-l-4 border-yellow-600 p-4 mb-4">
                <h4 className="font-bold text-yellow-800 mb-2">Additional Remedies</h4>
                <p className="text-gray-700">
                  Liquidated damages are in addition to, and not in lieu of, any other remedies available to PricePAL Compare, including:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                  <li>Actual damages exceeding liquidated amounts</li>
                  <li>Injunctive or other equitable relief</li>
                  <li>Recovery of legal fees and costs</li>
                  <li>Criminal referral where applicable</li>
                  <li>Statutory damages under applicable IP laws</li>
                </ul>
              </div>

              <p className="text-sm text-gray-600">
                You acknowledge that you have had the opportunity to review these liquidated damages provisions and agree that they are reasonable and proportionate to the potential harm caused by violations.
              </p>
            </section>

            {/* Section 9: Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property Rights</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.1 Ownership</h3>
              <p className="mb-4">
                All Content, the Database, software, algorithms, designs, graphics, logos, trademarks, trade names, and all other intellectual property associated with the Service are owned by or licensed to PricePAL Compare Ltd and are protected by:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Copyright, Designs and Patents Act 1988</li>
                <li>UK Database Rights (Copyright and Rights in Databases Regulations 1997)</li>
                <li>Trade Marks Act 1994</li>
                <li>Computer Misuse Act 1990</li>
                <li>Applicable EU and international intellectual property laws</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.2 Database Rights</h3>
              <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-4">
                <p className="font-semibold text-purple-800 mb-2">UK DATABASE RIGHTS NOTICE</p>
                <p className="text-gray-700">
                  Our Database constitutes a "database" within the meaning of the Copyright and Rights in Databases Regulations 1997. PricePAL Compare is the maker of the Database and owns all database rights therein. Extraction and/or re-utilization of the whole or a substantial part of the Database is strictly prohibited without our express written consent.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">9.3 Limited License</h3>
              <p className="mb-4">
                Subject to your compliance with this Agreement, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your personal, non-commercial purposes only. This license does not include any right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Reproduce, distribute, publicly display, or publicly perform any Content</li>
                <li>Modify, create derivative works from, or reverse engineer any aspect of the Service</li>
                <li>Use any data mining, robots, or similar data gathering methods</li>
                <li>Download (other than page caching) or copy Content except as expressly permitted</li>
                <li>Use the Service or Content for any commercial purpose</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-4">9.4 Trademarks</h3>
              <p>
                "PricePAL", the PricePAL logo, and all related names, logos, product and service names, designs, and slogans are trademarks of PricePAL Compare Ltd. You may not use these marks without our prior written permission.
              </p>
            </section>

            {/* Section 10: Indemnification */}
            <section className="bg-gray-100 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
              
              <p className="mb-4">
                You agree to defend, indemnify, and hold harmless PricePAL Compare Ltd, its officers, directors, employees, agents, licensors, suppliers, and any third-party information providers from and against all claims, losses, expenses, damages, and costs, including reasonable legal fees, arising from or related to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your violation of this Agreement</li>
                <li>Your use or misuse of the Service</li>
                <li>Your violation of any third-party rights, including intellectual property rights</li>
                <li>Any Content you submit or transmit through the Service</li>
                <li>Your violation of any applicable law or regulation</li>
                <li>Any activity conducted through your account</li>
              </ul>
              <p className="mt-4">
                We reserve the right to assume the exclusive defense and control of any matter subject to indemnification by you, at your expense.
              </p>
            </section>

            {/* Section 11: Disclaimers & Limitations */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Disclaimers & Limitation of Liability</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">11.1 Service "As Is"</h3>
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <p className="uppercase text-sm">
                  THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO THE FULLEST EXTENT PERMITTED BY LAW, PRICEPAL DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">11.2 No Guarantee of Accuracy</h3>
              <p className="mb-4">
                We do not warrant that Content, including price information, is accurate, complete, reliable, current, or error-free. Prices displayed are for informational purposes only and may differ from actual retailer prices.
              </p>
            </section>

            {/* Section 12: Termination */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Termination</h2>
              <p className="mb-4">
                We may suspend or terminate your account immediately, without prior notice, if you breach any provision of this Agreement.
              </p>
            </section>

            {/* Section 13: Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="mb-4">
                This Agreement shall be governed by and construed in accordance with the laws of England and Wales.
              </p>
            </section>

            {/* Section 14: General Provisions */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. General Provisions</h2>
              <p className="mb-4">
                This Agreement, together with the Privacy Policy, constitutes the entire agreement between you and PricePAL Compare regarding the Service.
              </p>
            </section>

            {/* Section 20: Savings Club — Loyalty & Referral Rewards — updated 2026-07-23 (PAUSED) */}
            <section data-testid="terms-section-loyalty-rewards">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">20. Savings Club — Loyalty &amp; Referral Rewards Programme</h2>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4" data-testid="terms-savings-club-paused-notice">
                <p className="font-semibold text-amber-900 mb-2">
                  ⏸ Programme Paused (effective 23 July 2026)
                </p>
                <p className="text-sm text-amber-900">
                  The Savings Club is currently <strong>paused while we finalise our rewards catalogue</strong>. During this pause:
                </p>
                <ul className="list-disc pl-5 mt-2 text-sm text-amber-900 space-y-1">
                  <li><strong>No new points can be earned</strong> from receipts, referrals, or any other activity.</li>
                  <li><strong>No rewards can be redeemed</strong> — the redemption flow is disabled.</li>
                  <li>Any existing point balances are preserved and will remain in your account when the Programme resumes.</li>
                  <li>All other app features (price comparison, receipt uploads, fuel finder, etc.) continue to work normally.</li>
                </ul>
                <p className="text-sm text-amber-900 mt-2">
                  The provisions below describe the Programme as it will operate when it resumes. They are provided here for transparency and will apply again on the relaunch date, which we will announce by in-app notification and email.
                </p>
              </div>

              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-4">
                <p className="font-semibold text-emerald-900 mb-2">
                  Plain-English summary (applies when Programme is active)
                </p>
                <p className="text-sm text-emerald-900">
                  PricePAL Compare does <strong>not</strong> pay cash, gift cards, vouchers, cheques, bank transfers, PayPal credit, cryptocurrency, or any other monetary form to End Users under our Savings Club programme. All user-side rewards are limited to <strong>(a) non-monetary gamification points</strong> displayed inside the app and <strong>(b) free Premium subscription time</strong> credited automatically to your account.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.1 Programme Overview</h3>
              <p className="mb-4">
                The PricePAL Compare Savings Club (the "Programme") rewards Eligible Users for contributing quality receipt data and for inviting new genuine users to the Service. Rewards are issued exclusively in two non-monetary forms:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-1">
                <li><strong>Savings Club Points</strong> — an in-app gamification metric. Points have no cash value, are not transferable, and cannot be redeemed for cash, vouchers, gift cards, or any other monetary instrument.</li>
                <li><strong>Free Premium Subscription Time</strong> — additional months of access to PricePAL Compare Premium, automatically credited to your account by extending your subscription end date.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.2 How You Earn Points</h3>
              <p className="mb-2">
                Points are awarded automatically after PricePAL Compare validates the underlying activity. Base amounts are shown below; your subscription tier multiplier and contributor level may increase the actual award.
              </p>
              <table className="w-full border border-gray-300 mb-4 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left">Activity</th>
                    <th className="border border-gray-300 px-3 py-2 text-left">Base points</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-3 py-2">Approved receipt (minimum 5 qualifying items)</td>
                    <td className="border border-gray-300 px-3 py-2">5</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 py-2">Approved barcode / product scan</td>
                    <td className="border border-gray-300 px-3 py-2">10</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 py-2">Referred friend signs up AND uploads their first fully-approved receipt</td>
                    <td className="border border-gray-300 px-3 py-2">100</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 py-2">Referred friend upgrades to any paid tier</td>
                    <td className="border border-gray-300 px-3 py-2">200</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-3 py-2">Referred friend remains on a paid tier for 3 months</td>
                    <td className="border border-gray-300 px-3 py-2">100</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.3 Subscription-Tier Multipliers</h3>
              <p className="mb-2">
                Paying members earn faster. The following multipliers stack with your contributor-level multiplier and the OCR trust score for each submission:
              </p>
              <table className="w-full border border-gray-300 mb-4 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left">Plan</th>
                    <th className="border border-gray-300 px-3 py-2 text-left">Points multiplier</th>
                    <th className="border border-gray-300 px-3 py-2 text-left">Marketing shorthand</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border border-gray-300 px-3 py-2">Free</td><td className="border border-gray-300 px-3 py-2">1.0×</td><td className="border border-gray-300 px-3 py-2">—</td></tr>
                  <tr><td className="border border-gray-300 px-3 py-2">Premium (including Founder, VIP, Community, NHS/Blue Light)</td><td className="border border-gray-300 px-3 py-2">1.5×</td><td className="border border-gray-300 px-3 py-2">"50% more points"</td></tr>
                  <tr><td className="border border-gray-300 px-3 py-2">Family</td><td className="border border-gray-300 px-3 py-2">2.0×</td><td className="border border-gray-300 px-3 py-2">"Double points"</td></tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.4 Milestone Bonuses</h3>
              <p className="mb-2">
                A one-time bonus is awarded when your <strong>fully-approved receipt count</strong> (receipts where every scanned item was approved) reaches each of the following thresholds. Bonuses fire exactly once and cannot be re-earned:
              </p>
              <table className="w-full border border-gray-300 mb-4 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left">Milestone</th>
                    <th className="border border-gray-300 px-3 py-2 text-left">Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border border-gray-300 px-3 py-2">First 25 fully-approved receipts</td><td className="border border-gray-300 px-3 py-2">+100 points</td></tr>
                  <tr><td className="border border-gray-300 px-3 py-2">First 100 fully-approved receipts</td><td className="border border-gray-300 px-3 py-2">+300 points</td></tr>
                  <tr><td className="border border-gray-300 px-3 py-2">First 250 fully-approved receipts</td><td className="border border-gray-300 px-3 py-2">+600 points</td></tr>
                  <tr><td className="border border-gray-300 px-3 py-2">First successful referral (referred user upgrades to paid)</td><td className="border border-gray-300 px-3 py-2">+100 points</td></tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.5 Redemption Catalogue</h3>
              <table className="w-full border border-gray-300 mb-4 text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-3 py-2 text-left">Reward</th>
                    <th className="border border-gray-300 px-3 py-2 text-left">Points cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border border-gray-300 px-3 py-2">3 months Premium — added to your subscription end date</td><td className="border border-gray-300 px-3 py-2">8,000</td></tr>
                  <tr><td className="border border-gray-300 px-3 py-2">6 months Premium — added to your subscription end date</td><td className="border border-gray-300 px-3 py-2">15,000</td></tr>
                </tbody>
              </table>
              <p className="mb-4 text-sm text-gray-700">
                Redemptions are irrevocable. Free Premium months extend the renewal date of an active subscription, or activate a Premium window from the redemption date for accounts on the Free tier (which then revert to Free at the end of the window).
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.6 Fair-Use &amp; Anti-Abuse Rules</h3>
              <p className="mb-2">To protect the Programme from farming, the following limits apply:</p>
              <ul className="list-disc pl-6 mb-4 space-y-1 text-sm">
                <li><strong>Minimum-item rule</strong>: Only receipts with 5 or more qualifying items are eligible for the receipt-approved award. Receipts with fewer items are still ingested to improve community price data but earn zero points.</li>
                <li><strong>Retailer cooldown</strong>: One rewarded receipt per retailer per 30 minutes. Additional receipts at the same retailer inside the cooldown window are stored for data but earn zero points.</li>
                <li><strong>Daily rewarded-receipt cap</strong>: Maximum 3 rewarded receipts per calendar day (UTC). Additional receipts are accepted but earn zero points until the cap resets at 00:00 UTC.</li>
                <li><strong>Duplicate detection</strong>: Receipts that match another receipt of yours on retailer + date + total + item lines will be flagged and, at PricePAL Compare's discretion, may not be awarded.</li>
                <li><strong>Points are awarded only after full approval</strong> of the receipt (every item on the receipt must be approved to count towards milestones).</li>
                <li><strong>Referral genuineness</strong>: Referred users must be new, distinct persons. Self-referrals, household duplicates, and any incentivised mass-signup mechanism are prohibited and will be reversed. PricePAL Compare may request identity verification for suspected fraud.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.7 No Monetary Value — Strictly No Cash Payouts</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                <p className="mb-2">
                  <strong>Savings Club Points and Free Premium Months have no cash value</strong>, are not assets, securities, or financial instruments, and cannot be:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Exchanged for cash, cheque, bank transfer, PayPal credit, cryptocurrency, or any other monetary instrument;</li>
                  <li>Sold, transferred, gifted, bequeathed, assigned, or otherwise disposed of to any third party;</li>
                  <li>Combined with the rewards balance of another PricePAL Compare account;</li>
                  <li>Reclaimed, refunded, or compensated for if an account is terminated, suspended, or closed (whether by you or by us).</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.8 Programme Changes &amp; Termination</h3>
              <p className="mb-4">
                We may modify, suspend, or terminate the Programme (in whole or in part) at any time and for any reason, with reasonable prior notice via in-app communication or email. Modifications may include adjusting reward tiers, milestone thresholds, tier multipliers, the duration of Free Premium Months, prize-draw mechanics, eligibility criteria, or anti-fraud rules. Points already in your balance and Free Premium Months already credited to your account at the time of any modification will be honoured on the terms in force when they were earned or credited.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">20.9 Tax &amp; Legal Considerations</h3>
              <p className="mb-4 text-sm text-gray-700">
                Because Programme rewards consist solely of non-monetary in-app credit, there are typically no income-tax implications for UK consumer participants. However, you are solely responsible for any tax obligations arising from your participation in the Programme. PricePAL Compare does not issue HMRC-reportable tax documents in connection with the Programme.
              </p>
            </section>

            {/* Section 22: Founder's Club & Beta Programme — added 2026-06-04 */}
            <section data-testid="terms-section-founders-club">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">22. Founder's Club &amp; Beta Programme</h2>
              <p className="mb-4">
                Subject to availability and at PricePAL Compare's sole discretion, a limited number of early adopters may be invited to join the <strong>Founder's Club</strong> or our closed <strong>Beta Programme</strong>. Members of these groups may receive lifetime or fixed-term discounts on their subscription. These discounts are <strong>not</strong> Programme rewards under Section 20 — they are commercial pricing offered directly by PricePAL Compare in exchange for early feedback, testing, and community support.
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-1 text-sm">
                <li>Founder's Club and Beta Programme discounts are tied to the specific account that earned them and are non-transferable.</li>
                <li>Discounts may be revoked for breach of these Terms, fraud, prolonged inactivity, or material change to your subscription tier.</li>
                <li>We may close enrolment to either programme at any time without notice.</li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage;
